import { useMenu } from '../context/MenuContext';
import { useCart } from '../context/CartContext';
import { useI18n } from '../i18n';
import { ZoneMap } from './ZoneMap';

/* Inline SVGs — small, neutral line icons coloured via currentColor.
   Kept inline so the widget has no external icon-font dependency. */
const Icons = {
  delivery: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="11" height="10" rx="1" />
      <path d="M13 9h4l4 4v3h-2" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  card: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
      <path d="M2.5 10h19" />
      <path d="M6 15h3" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 4h4l2 5-3 2a12 12 0 0 0 5 5l2-3 5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </svg>
  ),
};

interface InfoCardProps {
  icon: keyof typeof Icons;
  label: string;
  children: React.ReactNode;
}

function InfoCard({ icon, label, children }: InfoCardProps) {
  return (
    <div className="hc-info-card">
      <div className="hc-info-card-icon">{Icons[icon]}</div>
      <div className="hc-info-card-body">
        <span className="hc-info-card-label">{label}</span>
        <div className="hc-info-card-value">{children}</div>
      </div>
    </div>
  );
}

export function InfoPage() {
  const { business, onlineStatus, deliveryZones, googleApiKey } = useMenu();
  const { setView } = useCart();
  const { t } = useI18n();

  const address = business ? [business.address, business.city].filter(Boolean).join(', ') : '';
  const hasZones = deliveryZones && deliveryZones.length > 0;
  const hasSchedule = !!(onlineStatus?.schedule && onlineStatus.schedule.length > 0);

  return (
    <div className="hc-info-page">
      <div className="hc-info-header">
        <button className="hc-back-btn" onClick={() => setView('menu')}>&larr; {t('info.back')}</button>
        <h2>{t('info.title')}</h2>
      </div>

      <div className="hc-info-body">
        {googleApiKey && hasZones && (
          <section className="hc-info-section hc-info-section--map">
            <ZoneMap zones={deliveryZones} googleApiKey={googleApiKey} />
          </section>
        )}

        <div className="hc-info-grid">
          {hasZones && (
            <InfoCard icon="delivery" label={t('info.deliveryZones')}>
              <table className="hc-info-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{t('info.zoneMin')}</th>
                    <th>{t('info.zoneFee')}</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryZones.map((zone) => (
                    <tr key={zone.id}>
                      <td className="hc-info-zone-name">{zone.name}</td>
                      <td>{zone.minimum_order != null ? `${zone.minimum_order.toFixed(2)} €` : '—'}</td>
                      <td>
                        {zone.delivery_fee_item
                          ? `${Number(zone.delivery_fee_item.price).toFixed(2)} €`
                          : t('info.zoneFree')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </InfoCard>
          )}

          {hasSchedule && (
            <InfoCard icon="clock" label={t('info.deliveryHours')}>
              <ul className="hc-info-card-list">
                {onlineStatus!.schedule.map((entry) => (
                  <li
                    key={entry.day}
                    className={`hc-info-card-list-row ${!entry.active ? 'hc-inactive' : ''}`}
                  >
                    <span className="hc-day">{t(`day.${entry.day}` as any)}</span>
                    <span className="hc-hours">
                      {entry.active ? `${entry.open} – ${entry.close}` : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </InfoCard>
          )}

          <InfoCard icon="card" label={t('info.paymentMethods')}>
            <ul className="hc-info-card-list">
              <li className="hc-info-card-list-row"><span className="hc-day">{t('checkout.paymentCash')}</span></li>
              <li className="hc-info-card-list-row"><span className="hc-day">{t('checkout.paymentCard')}</span></li>
            </ul>
          </InfoCard>

          {address && (
            <InfoCard icon="pin" label={t('info.address')}>
              {address}
            </InfoCard>
          )}

          {business?.number && (
            <InfoCard icon="phone" label={t('info.phone')}>
              <a href={`tel:${business.number}`}>{business.number}</a>
            </InfoCard>
          )}
        </div>
      </div>
    </div>
  );
}
